import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { Settings, Mail, Phone, Truck, CreditCard, Globe, Save, CheckCircle, XCircle } from 'lucide-react';

const SystemSettingsEnhanced = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('email');
  const [saving, setSaving] = useState(false);
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const baseUrl = API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/admin/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (category, updatedSettings) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const baseUrl = API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ category, settings: updatedSettings })
      });

      if (response.ok) {
        alert('Settings updated successfully');
        fetchSettings();
      } else {
        alert('Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Error updating settings');
    } finally {
      setSaving(false);
    }
  };

  const testEmail = async (email) => {
    try {
      const token = localStorage.getItem('adminToken');
      const baseUrl = API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/admin/settings/test-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setTestResults({ ...testResults, email: 'success' });
        alert('Test email sent successfully!');
      } else {
        setTestResults({ ...testResults, email: 'error' });
        alert('Failed to send test email');
      }
    } catch (error) {
      setTestResults({ ...testResults, email: 'error' });
      alert('Error sending test email');
    }
  };

  const testSMS = async (phone) => {
    try {
      const token = localStorage.getItem('adminToken');
      const baseUrl = API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/admin/settings/test-sms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone })
      });

      if (response.ok) {
        setTestResults({ ...testResults, sms: 'success' });
        alert('Test SMS sent successfully!');
      } else {
        setTestResults({ ...testResults, sms: 'error' });
        alert('Failed to send test SMS');
      }
    } catch (error) {
      setTestResults({ ...testResults, sms: 'error' });
      alert('Error sending test SMS');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d44100]"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <Settings className="h-6 w-6 mr-2 text-[#d44100]" />
          System Settings
        </h2>
        <p className="text-sm text-gray-600 mt-1">Configure system-wide settings and integrations</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6 border-b">
        {['email', 'sms', 'shipping', 'payment', 'general'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium capitalize ${
              activeTab === tab
                ? 'border-b-2 border-[#d44100] text-[#d44100]'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            {tab === 'email' && <Mail className="h-4 w-4 inline mr-2" />}
            {tab === 'sms' && <Phone className="h-4 w-4 inline mr-2" />}
            {tab === 'shipping' && <Truck className="h-4 w-4 inline mr-2" />}
            {tab === 'payment' && <CreditCard className="h-4 w-4 inline mr-2" />}
            {tab === 'general' && <Globe className="h-4 w-4 inline mr-2" />}
            {tab}
          </button>
        ))}
      </div>

      {/* Email Settings */}
      {activeTab === 'email' && settings?.email && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Email Configuration</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
              <input
                type="text"
                defaultValue={settings.email.smtpHost}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
                placeholder="smtp.gmail.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
                <input
                  type="number"
                  defaultValue={settings.email.smtpPort}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From Email</label>
                <input
                  type="email"
                  defaultValue={settings.email.fromEmail}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">From Name</label>
              <input
                type="text"
                defaultValue={settings.email.fromName}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
              />
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  const email = prompt('Enter email address to test:');
                  if (email) testEmail(email);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                {testResults.email === 'success' ? (
                  <CheckCircle className="h-4 w-4 mr-2" />
                ) : testResults.email === 'error' ? (
                  <XCircle className="h-4 w-4 mr-2" />
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                Test Email
              </button>
              <button
                onClick={() => updateSettings('email', settings.email)}
                disabled={saving}
                className="px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700] disabled:opacity-50 flex items-center"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SMS Settings */}
      {activeTab === 'sms' && settings?.sms && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">SMS Configuration</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
              <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]">
                <option value="africastalking">Africa's Talking</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
              <input
                type="text"
                defaultValue={settings.sms.username}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Short Code</label>
              <input
                type="text"
                defaultValue={settings.sms.shortCode}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
              />
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  const phone = prompt('Enter phone number to test (254XXXXXXXXX):');
                  if (phone) testSMS(phone);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                {testResults.sms === 'success' ? (
                  <CheckCircle className="h-4 w-4 mr-2" />
                ) : testResults.sms === 'error' ? (
                  <XCircle className="h-4 w-4 mr-2" />
                ) : (
                  <Phone className="h-4 w-4 mr-2" />
                )}
                Test SMS
              </button>
              <button
                onClick={() => updateSettings('sms', settings.sms)}
                disabled={saving}
                className="px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700] disabled:opacity-50 flex items-center"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shipping Settings */}
      {activeTab === 'shipping' && settings?.shipping && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Shipping Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Free Shipping Threshold (KES)</label>
              <input
                type="number"
                defaultValue={settings.shipping.freeShippingThreshold}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">Shipping Rates by County</label>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(settings.shipping.rates).map(([county, rate]) => (
                  <div key={county} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-700 capitalize">{county}</span>
                    <input
                      type="number"
                      defaultValue={rate}
                      className="w-24 px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
                    />
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={() => updateSettings('shipping', settings.shipping)}
              disabled={saving}
              className="px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700] disabled:opacity-50 flex items-center"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </button>
          </div>
        </div>
      )}

      {/* Payment Settings */}
      {activeTab === 'payment' && settings?.payment && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Payment Gateway Settings</h3>
          <div className="space-y-6">
            <div>
              <h4 className="font-medium text-gray-800 mb-3">M-Pesa Configuration</h4>
              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Short Code</label>
                  <input
                    type="text"
                    defaultValue={settings.payment.mpesa.businessShortCode}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
                    readOnly
                  />
                </div>
                <p className="text-xs text-gray-500">M-Pesa credentials are configured via environment variables</p>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-800 mb-3">Stripe Configuration</h4>
              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-500">Stripe credentials are configured via environment variables</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* General Settings */}
      {activeTab === 'general' && settings?.general && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">General Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
              <input
                type="text"
                defaultValue={settings.general.siteName}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                <input
                  type="text"
                  defaultValue={settings.general.currency}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tax Rate (%)</label>
                <input
                  type="number"
                  defaultValue={settings.general.taxRate}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Order Prefix</label>
              <input
                type="text"
                defaultValue={settings.general.orderPrefix}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
              />
            </div>
            <button
              onClick={() => updateSettings('general', settings.general)}
              disabled={saving}
              className="px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700] disabled:opacity-50 flex items-center"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettingsEnhanced;







